/**
 * feedbackApi.ts
 * Client for the feedback intake and status endpoints.
 * Matches the contract defined in SPEC-feedback-001 §4.4.
 */

import { apiClient } from './apiClient'
import type { DiagnosticsSnapshot } from '../components/feedback/diagnosticsBuffer'

export type FeedbackArea =
  | 'programs'
  | 'database-sources'
  | 'usage'
  | 'workbench'
  | 'docs'
  | 'org-settings'
export type FeedbackSeverity = 'low' | 'normal' | 'blocking'
export type FeedbackOpsStatusStage =
  | 'new'
  | 'triaged'
  | 'assigned'
  | 'analyzing'
  | 'working'
  | 'in-review'
  | 'done'
  | 'blocked'
  | 'needs-info'
  | 'duplicate'
  | 'wont-fix'

export interface FeedbackContext {
  route: string
  appSha: string
  browser: string
  os: string
}

export interface SubmitFeedbackPayload {
  area: FeedbackArea
  severity: FeedbackSeverity
  whatHappened: string
  whatExpected: string
  context: FeedbackContext
  screenshot?: string   // base64 data URL, optional
  diagnostics?: DiagnosticsSnapshot  // console + network logs for current page
  idempotencyKey: string
}

export interface SubmitFeedbackResponse {
  ticketId: string | number
  statusUrl: string
}

export interface FeedbackTicket {
  ticketId: string | number
  area: FeedbackArea
  severity: FeedbackSeverity
  title: string
  state: string
  statusText: string
  createdAt: string
}

export interface FeedbackTicketReport {
  whatHappened: string | null
  whatExpected: string | null
  route: string | null
  appSha: string | null
  browser: string | null
  os: string | null
  diagnostics: {
    available: boolean
    consoleCount: number | null
    networkCount: number | null
    capturedForRoute: string | null
  }
}

export interface FeedbackTimelineEvent {
  id: string
  label: string
  state: 'complete' | 'current' | 'pending'
  detail: string
  timestamp?: string
  source: 'issue' | 'label' | 'assignee' | 'comment'
}

export interface FeedbackLinkedPullRequest {
  number: number
  url: string | null
  state: 'open' | 'closed' | 'merged' | 'unknown'
  reviewStatus: string
  mergeStatus: string
  source: 'body' | 'comment'
}

/** Returned by GET /api/feedback/:ticketId — superset of FeedbackTicket */
export interface FeedbackTicketDetail extends FeedbackTicket {
  /** The full plain-language status message from the bot-owned comment. */
  fullStatusMessage: string
  /** GitHub issue URL, returned only by internal operations detail endpoints. */
  issueUrl?: string
  updatedAt: string
  priority: string
  reporter: string | null
  report: FeedbackTicketReport
  timeline: FeedbackTimelineEvent[]
  linkedPullRequest: FeedbackLinkedPullRequest | null
  canReporterClose: boolean
  /** Publicly accessible URL of the attached screenshot, if one was uploaded. */
  screenshotUrl?: string
  /** GitHub login of the person/bot assigned to this issue; null if unassigned. */
  assignee?: string | null
}

export interface FeedbackOpsTicket {
  ticketId: number
  title: string
  state: 'open' | 'closed'
  area: string
  severity: string
  priority: string
  statusStage: FeedbackOpsStatusStage
  statusText: string
  assignee: string | null
  reporter: string | null
  createdAt: string
  updatedAt: string
  labels: string[]
  hasScreenshot: boolean
  issueUrl: string
  /** Live Claude session a routine opened for this issue, if any. */
  sessionUrl: string | null
  sessionPhase: 'triage' | 'implementation' | null
  sessionStartedAt: string | null
}

export type FeedbackAutomationMode = 'auto' | 'manual'

export interface FeedbackOpsQuery {
  status?: string
  stage?: string
  assignee?: string
  area?: string
  severity?: string
  state?: 'open' | 'closed'
  search?: string
  page?: number
  pageSize?: number
}

export interface FeedbackOpsQueueResult {
  items: FeedbackOpsTicket[]
  total: number
  page: number
  pageSize: number
}

export interface FeedbackRoutineStartResponse {
  sessionId: string
  sessionUrl: string
}

/**
 * Derives a lifecycle stage key from the plain-English statusText produced by
 * the backend label→statusText map (§5.2).  Matched by prefix/keyword so the
 * display layer is decoupled from label internals.
 */
export type FeedbackStatusStage =
  | 'logged'
  | 'acknowledged'
  | 'working'
  | 'fix-ready'
  | 'shipped'
  | 'needs-info'

export function deriveStatusStage(statusText: string): FeedbackStatusStage {
  const lower = statusText.toLowerCase()
  if (lower.startsWith('shipped') || lower.includes('please re-test') || lower.includes('released')) return 'shipped'
  if (lower.includes('fix ready') || lower.includes('pending human') || lower.includes('in-review')) return 'fix-ready'
  if (lower.includes('needs info') || lower.includes('needs-info') || lower.includes('needs more')) return 'needs-info'
  if (lower.includes('working on it') || lower.includes('fix in progress') || lower.includes('root cause') || lower.includes('analyzing')) return 'working'
  if (lower.includes('acknowledged') || lower.includes('assigned') || lower.includes('triaged')) return 'acknowledged'
  return 'logged'
}

function buildQuery(params: FeedbackOpsQuery): string {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    query.set(key, String(value))
  })
  const raw = query.toString()
  return raw ? `?${raw}` : ''
}

export const feedbackApi = {
  /** POST /api/feedback — submit a new feedback report */
  submit: (payload: SubmitFeedbackPayload): Promise<SubmitFeedbackResponse> =>
    apiClient.post('/api/feedback', payload),

  /** GET /api/feedback/mine — list current user's tickets */
  mine: (): Promise<FeedbackTicket[]> =>
    apiClient.get('/api/feedback/mine'),

  /** GET /api/feedback/ops — internal operations queue */
  ops: (query: FeedbackOpsQuery = {}): Promise<FeedbackOpsQueueResult> =>
    apiClient.get(`/api/feedback/ops${buildQuery(query)}`),

  /** GET /api/feedback/:ticketId — single ticket detail */
  getTicket: (ticketId: string | number): Promise<FeedbackTicketDetail> =>
    apiClient.get(`/api/feedback/${ticketId}`),

  /** GET /api/feedback/ops/:ticketId — internal operations ticket detail */
  getOpsTicket: (ticketId: string | number): Promise<FeedbackTicketDetail> =>
    apiClient.get(`/api/feedback/ops/${ticketId}`),

  /** POST /api/feedback/ops/:ticketId/run-triage — start the Claude triage routine */
  runTriage: (ticketId: string | number): Promise<FeedbackRoutineStartResponse> =>
    apiClient.post(`/api/feedback/ops/${ticketId}/run-triage`, {}),

  /** POST /api/feedback/ops/:ticketId/run-implementation — start the Claude implementation routine */
  runImplementation: (ticketId: string | number): Promise<FeedbackRoutineStartResponse> =>
    apiClient.post(`/api/feedback/ops/${ticketId}/run-implementation`, {}),

  /** GET /api/feedback/ops/automation-mode — current Auto/Manual automation mode */
  getAutomationMode: (): Promise<{ mode: FeedbackAutomationMode }> =>
    apiClient.get('/api/feedback/ops/automation-mode'),

  /** PUT /api/feedback/ops/automation-mode — switch the automation mode */
  setAutomationMode: (mode: FeedbackAutomationMode): Promise<{ mode: FeedbackAutomationMode }> =>
    apiClient.put('/api/feedback/ops/automation-mode', { mode }),

  /** POST /api/feedback/:ticketId/close — reporter confirms the fix and closes the ticket */
  closeTicket: (ticketId: string | number): Promise<{ ticketId: number; state: 'closed' }> =>
    apiClient.post(`/api/feedback/${ticketId}/close`, {}),
}
